import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Mail, Phone, Calendar, Tag, MessageSquare, CheckCircle, Clock, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  new: { label: "New", color: "bg-blue-500", icon: Clock },
  contacted: { label: "Contacted", color: "bg-yellow-500", icon: MessageSquare },
  resolved: { label: "Resolved", color: "bg-green-500", icon: CheckCircle },
  closed: { label: "Closed", color: "bg-slate-500", icon: XCircle },
};

const serviceLabels = {
  "phone-tablet": "Phone & Tablet Repair",
  "computer": "Computer Repair & Builds",
  "console": "Console Repair & Modification",
  "retro": "Retro Console Restoration",
  "other": "Other / Not Sure"
};

const AdminInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  const fetchInquiries = async () => {
    try {
      const response = await axios.get(`${API}/contact`);
      setInquiries(response.data);
    } catch (error) {
      toast.error("Failed to load inquiries");
      console.error("Error fetching inquiries:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/contact/${id}?status=${newStatus}`);
      setInquiries(prev => 
        prev.map(inq => inq.id === id ? { ...inq, status: newStatus } : inq)
      );
      if (selectedInquiry?.id === id) {
        setSelectedInquiry(prev => ({ ...prev, status: newStatus }));
      }
      toast.success("Status updated");
    } catch (error) {
      toast.error("Failed to update status");
      console.error("Error updating status:", error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b1120] flex items-center justify-center">
        <div className="text-slate-400 font-mono">Loading inquiries...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b1120] text-slate-100">
      {/* Header */}
      <header className="border-b border-slate-800 bg-[#0f172a]" data-testid="admin-header">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a href="/" className="text-slate-400 hover:text-white transition-colors">
                <ArrowLeft size={20} />
              </a>
              <h1 className="text-xl font-bold font-mono uppercase tracking-tight">
                Contact Inquiries<span className="text-red-500">_</span>
              </h1>
            </div>
            <Badge variant="outline" className="border-slate-700 text-slate-400">
              {inquiries.length} Total
            </Badge>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {inquiries.length === 0 ? (
          <div className="text-center py-16 border border-slate-800 bg-slate-900/30">
            <MessageSquare className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No inquiries yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Inquiry List */}
            <div className="lg:col-span-1 space-y-3" data-testid="inquiry-list">
              {inquiries.map(inquiry => {
                const StatusIcon = statusConfig[inquiry.status]?.icon || Clock;
                return (
                  <button
                    key={inquiry.id}
                    onClick={() => setSelectedInquiry(inquiry)}
                    className={`w-full text-left p-4 border transition-all ${
                      selectedInquiry?.id === inquiry.id 
                        ? 'border-red-500 bg-slate-800/50' 
                        : 'border-slate-800 bg-slate-900/30 hover:border-slate-700'
                    }`}
                    data-testid={`inquiry-item-${inquiry.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-200 truncate">{inquiry.name}</p>
                        <p className="text-sm text-slate-500 truncate">{inquiry.email}</p>
                      </div>
                      <div className={`w-2 h-2 rounded-full ${statusConfig[inquiry.status]?.color || 'bg-slate-500'}`} />
                    </div>
                    <p className="text-xs text-slate-600 mt-2 font-mono">
                      {formatDate(inquiry.created_at)}
                    </p>
                  </button>
                );
              })}
            </div>

            {/* Inquiry Detail */}
            <div className="lg:col-span-2" data-testid="inquiry-detail">
              {selectedInquiry ? (
                <div className="border border-slate-800 bg-slate-900/30 p-6 md:p-8">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-slate-100 mb-1">
                        {selectedInquiry.name}
                      </h2>
                      <p className="text-slate-500 text-sm font-mono">
                        ID: {selectedInquiry.id.slice(0, 8)}...
                      </p>
                    </div>
                    <Select 
                      value={selectedInquiry.status} 
                      onValueChange={(value) => updateStatus(selectedInquiry.id, value)}
                    >
                      <SelectTrigger className="w-36 bg-slate-800 border-slate-700" data-testid="status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                        {Object.entries(statusConfig).map(([key, config]) => (
                          <SelectItem 
                            key={key} 
                            value={key}
                            className="text-slate-200 hover:bg-slate-800 focus:bg-slate-800"
                          >
                            <span className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${config.color}`} />
                              {config.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="flex items-center gap-3 text-slate-300">
                      <Mail className="w-5 h-5 text-red-500" />
                      <a href={`mailto:${selectedInquiry.email}`} className="hover:text-red-500 transition-colors">
                        {selectedInquiry.email}
                      </a>
                    </div>
                    {selectedInquiry.phone && (
                      <div className="flex items-center gap-3 text-slate-300">
                        <Phone className="w-5 h-5 text-red-500" />
                        <a href={`tel:${selectedInquiry.phone}`} className="hover:text-red-500 transition-colors">
                          {selectedInquiry.phone}
                        </a>
                      </div>
                    )}
                    <div className="flex items-center gap-3 text-slate-300">
                      <Calendar className="w-5 h-5 text-red-500" />
                      <span>{formatDate(selectedInquiry.created_at)}</span>
                    </div>
                    {selectedInquiry.service && (
                      <div className="flex items-center gap-3 text-slate-300">
                        <Tag className="w-5 h-5 text-red-500" />
                        <span>{serviceLabels[selectedInquiry.service] || selectedInquiry.service}</span>
                      </div>
                    )}
                  </div>

                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">
                      Message
                    </h3>
                    <div className="bg-slate-800/50 p-4 border border-slate-700">
                      <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">
                        {selectedInquiry.message}
                      </p>
                    </div>
                  </div>

                  <div className="mt-8 flex gap-3">
                    <Button 
                      asChild
                      className="bg-red-500 hover:bg-red-600 text-white font-bold uppercase tracking-widest px-6 py-5 rounded-none"
                    >
                      <a href={`mailto:${selectedInquiry.email}`}>
                        Reply via Email
                      </a>
                    </Button>
                    {selectedInquiry.phone && (
                      <Button 
                        asChild
                        variant="outline"
                        className="border-slate-600 hover:border-slate-400 text-slate-300 font-bold uppercase tracking-widest px-6 py-5 rounded-none bg-transparent"
                      >
                        <a href={`tel:${selectedInquiry.phone}`}>
                          Call
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="border border-slate-800 bg-slate-900/30 p-12 text-center">
                  <MessageSquare className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                  <p className="text-slate-500">Select an inquiry to view details</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInquiries;
